/**
 * Put back the USER row a send lost, without waiting for the reply.
 *
 * A `kind: 'new'` chat handoff (the task drill-in's "Resolve / Discuss / Follow up with {agent}") points
 * the surface at NO conversation, `send` lazy-creates one, and the parent reflects that new id back down
 * as `selectedId`. That selection change can run the message-load effect, which clears `messages` and
 * refetches — and the row the user just sent exists only optimistically at that moment. `use-chat`'s
 * post-turn consistency loop already knows this ("restores a USER bubble dropped during a
 * new-conversation handoff"), but it only adopts the server thread once the server has persisted THIS
 * TURN'S ASSISTANT REPLY, across three attempts at 2.5s / 5s / 10s. A long research turn does not reply
 * inside ~17.5s, so the loop runs out and nothing ever repairs it.
 *
 * The user's own row does NOT depend on the reply: the server persists it when the stream request opens.
 * So it can be restored on the first poll. This module decides that, and only that:
 *
 *   - **Restore, never adopt.** Replacing our list with the server page would delete the reply the reader
 *     has already watched stream in, whenever the server has not persisted it yet — the regression class
 *     the consistency loop's own comments warn about. We splice the one missing row in and leave every
 *     other row alone.
 *   - **Count, don't match.** Deciding by "the server has a row with this text and we don't" double-inserts
 *     when someone legitimately sends the same sentence twice. We compare how many copies each side holds
 *     to decide WHETHER to restore, and then pick a copy we are not already holding by id — otherwise a
 *     thread that said the same sentence in an earlier turn gets that older row duplicated while the
 *     genuinely missing one stays missing.
 *   - **Take the position from the SERVER's ordering.** The obvious-looking anchor — "insert above the
 *     first assistant row that is not pre-turn" — is wrong, because `use-chat` tracks only the LAST
 *     pre-turn assistant id, so on a thread with several earlier replies the first of them reads as this
 *     turn's and the restored row lands at the very top of the transcript. The server page already knows
 *     the true order, so we anchor to the last row it places BEFORE the restored one that we also hold.
 *
 * Pure and import-free on purpose: `packages/orion-web/CLAUDE.md` requires unit-tested logic to live in a
 * module that does not reach the app's dependency graph, because common-lib's barrel drags ESM-only deps
 * that resolve to `dist/cjs` locally and `src/` in CI — a test that imports the graph passes here and
 * fails there.
 *
 * Native needs no twin: the phones fixed the same report upstream, by making the seed WAIT for its
 * thread's load to settle (`AppRouter.openChatInConversation` / `Navigator.openChatInConversation`), so no
 * row is ever dropped there and there is nothing to put back. Web's create-then-reflect ordering is what
 * makes this the web shape of the fix — a written-down divergence, not a gap.
 */

/** The shape this module needs from a chat row; `use-chat`'s `Message` satisfies it structurally. */
export interface SeedRepairRow {
  id?: string;
  /**
   * The persisted server id of a row that arrived over the stream, whose `id` is a temporary local one.
   * Identity has to consider BOTH or a row we are holding reads as one we lack — `use-chat` guards its
   * own pre-turn snapshot the same way (`preTurnAssistantIds` adds `id` AND `finalId`).
   */
  finalId?: string;
  role?: string;
  content?: string;
}

const isUser = (row: SeedRepairRow) => row.role === 'USER';

/** Every id a row can be known by — its local one and, for a streamed row, its persisted one. */
const idsOf = (row: SeedRepairRow): string[] =>
  [row.id, row.finalId].filter((id): id is string => !!id);

function countSent(rows: readonly SeedRepairRow[], sentText: string): number {
  return rows.reduce((n, row) => (isUser(row) && row.content === sentText ? n + 1 : n), 0);
}

/**
 * The transcript with the sent row put back, or `null` when nothing needs repairing.
 *
 * `null` is the overwhelmingly common answer — the row is still there — so the caller can treat a
 * non-null result as "something was actually lost".
 *
 * @param ours     the rows we currently render
 * @param server   the freshly fetched server page
 * @param sentText the text this send submitted (empty for a voice note: nothing to key on, so no repair)
 */
export function restoreSentRow(
  ours: readonly SeedRepairRow[],
  server: readonly SeedRepairRow[],
  sentText: string,
): SeedRepairRow[] | null {
  if (!sentText) return null;
  // The server is only authoritative about rows it actually holds. It holding FEWER than us is the
  // ordinary case mid-turn (our optimistic row, not yet persisted) and must never remove anything.
  if (countSent(server, sentText) <= countSent(ours, sentText)) return null;

  // Among the server's copies, the one to restore is one we are not already holding — and the NEWEST
  // such, since the row that was dropped is this turn's.
  const heldIds = new Set(ours.flatMap(idsOf));
  let at = -1;
  for (let i = server.length - 1; i >= 0; i--) {
    const row = server[i];
    if (isUser(row) && row.content === sentText && !idsOf(row).some((id) => heldIds.has(id))) {
      at = i;
      break;
    }
  }
  if (at === -1) return null;

  // Anchor to the last row the server places before it that we also hold, so rows the page does not
  // cover (older history) and rows the server has not persisted yet (our streamed reply) both stay put.
  const precedingIds = new Set(server.slice(0, at).flatMap(idsOf));
  let insertAt: number | null = null;
  for (let i = 0; i < ours.length; i++) {
    if (idsOf(ours[i]).some((id) => precedingIds.has(id))) insertAt = i + 1;
  }
  // No anchor: if nothing precedes it on the server the row opens the thread, otherwise the safe end is
  // the bottom — below its own reply reads slightly off, above unrelated history reads as a bug.
  const index = insertAt ?? (at === 0 ? 0 : ours.length);
  return [...ours.slice(0, index), server[at], ...ours.slice(index)];
}
